import 'react-reflex/styles.css';
import clsx from 'clsx';
import _ from 'lodash';
import { forwardRef, useEffect, useState } from 'react';
import { Form, useFormState } from 'react-final-form';
import { CancelAndExecute } from '@/core/CancelAndExecute.molecule';
import { CancelSaveAndContinue } from '@/core/CancelSaveAndContinue.molecule';
import { renderIt } from '@/formbuilder/FormElementRenderer';
import { FlexLayoutSplitPanes, isFlexLayoutSplitter } from '@/formbuilder/formElements/FlexLayoutSplitPanes';
import {
  decorateWithFieldNames,
  decorateWithNumbers,
  flattenFormDefinition,
} from '@/formbuilder/formbuilder.utilities';
import { useForwardRef } from '@/formbuilder/hooks/useForwardRef';
import type React from 'react';
import type { ContinueButtonProps, FormElement } from '@/formbuilder/formBuilder.constants';
import type { ButtonVariant } from '@seeqdev/qomponents';

type FormValues = Record<string, unknown>;

const SubmitDisableEffect = ({
  enableSubmitBtn,
  setSubmitIsDisabled,
  onDirtyChange,
}: {
  enableSubmitBtn: boolean;
  setSubmitIsDisabled?: (value: boolean) => void;
  onDirtyChange?: (dirtyFields: string[]) => void;
}) => {
  const { submitFailed, modified } = useFormState({ subscription: { submitFailed: true, modified: true } });

  useEffect(() => {
    if (!enableSubmitBtn && submitFailed && setSubmitIsDisabled) {
      setSubmitIsDisabled(true);
    }
    if (onDirtyChange) {
      const dirtyFields = modified ? Object.keys(modified).filter((field) => modified[field]) : [];
      onDirtyChange(dirtyFields);
    }
  }, [enableSubmitBtn, submitFailed, setSubmitIsDisabled, modified, onDirtyChange]);
  return null;
};

export interface FormProps {
  testId?: string;
  /** the formDefinition Array of FormElements defines the form to be rendered **/
  formDefinition: FormElement[];
  /** option to have the submit button say something other than 'Execute' **/
  submitBtnLabel?: string;
  /** option to have different Save or Execute submit button **/
  submitBtnVariant?: ButtonVariant;
  /** option to have the cancel button say something other than 'Cancel' **/
  cancelBtnLabel?: string;
  /** CSS classes to apply to the cancel button **/
  cancelClassNames?: string;
  /** option to hide the cancel button **/
  hideCancel?: boolean;
  /** option to hide the submit button **/
  hideSubmit?: boolean;
  /** this is useful for those rare cases where the submit button should remain in a loading state after the submitFn is
   resolved (ex. oauth) **/
  continueProcessing?: boolean;
  /** option to pass a ref to access submit button status **/
  ref?: React.ForwardedRef<boolean | undefined>;
  /** extra class-names for action buttons **/
  buttonExtraClassNames?: string;
  /** the function that is called when the cancel button is clicked **/
  closeFn: () => void;
  setSubmitIsDisabled?: (value: boolean) => void;
  /** called whenever any form field has been modified by the user **/
  onDirtyChange?: (dirtyFields: string[]) => void;
  /** Adds a dropdown to the execute button to have multiple execution options  **/
  submitOptions?: {
    key: string;
    label: string;
    variant?: ButtonVariant;
    submitFn: (values: unknown) => unknown;
    onSelect?: () => void;
  }[];
  /** if true, the form will reset to the first submit option when submitted */
  resetToFirstSubmitOptionOnSubmit?: boolean;
  /** if debug is enabled then the form values and form errors are rendered below this form; **/
  debug?: boolean;
  /** Rendered at the start of the `saveAndCancel` button row, left-aligned while Cancel/Save/Continue
   *  stay grouped on the right. Ignored outside `saveAndCancel` mode. **/
  leadingButton?: React.ReactNode;
}

export interface FormBuilderProps extends ContinueButtonProps, FormProps {
  /** the function that is called when the form is submitted **/
  submitFn: (values: unknown) => void | Promise<unknown>;
  /** id of the tool (legacy - may no longer apply) **/
  toolId?: string;
  /** id of the submitBtnId (legacy - may no longer apply) **/
  submitBtnId?: string;
  /** disable the submit button **/
  submitBtnDisabled?: boolean;
  /** by default we assume we're creating a tool panel, however if we want to use a CancelAndSave as the button
 component we can do so by setting this to true **/
  saveAndCancel?: boolean;
  /** wrap the form in a panel **/
  wrapInPanel?: boolean;
  /** extra classnames applied if not wrapped in panel **/
  extraClassNamesUnwrapped?: string;
  /** option to hide the execute button **/
  hideExecute?: boolean;
  extraClassNames?: string;
}

const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => e.preventDefault();

type SubmitHandler = (arg?: unknown) => unknown;

export const FormBuilder = forwardRef<boolean | undefined, FormBuilderProps>(function FormBuilder(props, ref) {
  const formRef = useForwardRef(ref);
  const {
    testId,
    formDefinition,
    submitFn,
    closeFn,
    toolId,
    cancelBtnLabel,
    cancelClassNames,
    submitBtnId,
    submitBtnLabel,
    submitBtnVariant,
    submitBtnDisabled,
    debug = false,
    saveAndCancel = false,
    hideCancel = false,
    wrapInPanel = true,
    extraClassNamesUnwrapped,
    hideSubmit,
    hideExecute = false,
    buttonExtraClassNames,
    continueProcessing,
    setSubmitIsDisabled,
    continueButtonLabel,
    continueButtonEnabled,
    continueButtonOnSubmit,
    continueButtonShouldValidate = false,
    continueButtonVariant,
    continueButtonPosition,
    continueButtonIcon,
    continueButtonTooltip,
    continueButtonExtraClassNames,
    submitOptions,
    resetToFirstSubmitOptionOnSubmit,
    hideContinue,
    extraClassNames,
    onDirtyChange,
    leadingButton,
  } = props;

  const [isExtraButton, setIsExtraButton] = useState(false);
  const [activeSubmitKey, setActiveSubmitKey] = useState<string | undefined>(submitOptions?.[0]?.key);
  const flatFormDefinition: FormElement[] = flattenFormDefinition(formDefinition);
  const withFieldNames = decorateWithFieldNames(formDefinition);
  const withDisplayNumbers = decorateWithNumbers(withFieldNames);
  const formData: FormValues = _.chain(flatFormDefinition).keyBy('name').mapValues('value').value() as FormValues;

  const renderToolPanelForm = (handleSubmit: SubmitHandler, enableSubmitBtn: boolean) => {
    const handleSubmitOptions = submitOptions?.map((option) => ({
      ...option,
      submitFn: () => Promise.resolve().then(() => handleSubmit((values: FormValues) => option.submitFn(values))),
    }));
    const flexLayoutSplitterIndex = withDisplayNumbers.findIndex(
      (definition: FormElement) => definition.component === 'FlexLayoutSplitter',
    );
    const flexLayoutSplitter = isFlexLayoutSplitter(withDisplayNumbers[flexLayoutSplitterIndex])
      ? withDisplayNumbers[flexLayoutSplitterIndex]
      : undefined;

    if (flexLayoutSplitter) {
      return (
        <div className={wrapInPanel ? 'card card-primary tw:pb-sq-10' : extraClassNamesUnwrapped}>
          <FlexLayoutSplitPanes
            upper={withDisplayNumbers.slice(0, flexLayoutSplitterIndex)}
            lower={withDisplayNumbers.slice(flexLayoutSplitterIndex + 1)}
            upperPaneFlex={flexLayoutSplitter.upperPaneFlex}
            onUpperPaneFlexChange={flexLayoutSplitter.onUpperPaneFlexChange}
          />
          <div className={clsx(buttonExtraClassNames, { pb10: wrapInPanel })}>
            <CancelAndExecute
              cancelAction={closeFn}
              submitAction={async () => {
                await Promise.resolve().then(handleSubmit);
              }}
              toolId={toolId}
              btnDisabled={!enableSubmitBtn}
              cancelBtnLabel={cancelBtnLabel}
              submitBtnLabel={submitBtnLabel}
              hideCancel={hideCancel}
              submitBtnId={submitBtnId}
              hideExecute={hideExecute}
              submitOptions={handleSubmitOptions}
              activeSubmitKey={activeSubmitKey}
              setActiveSubmitKey={setActiveSubmitKey}
            />
          </div>
        </div>
      );
    }
    return (
      <div className={wrapInPanel ? 'card card-primary tw:pb-sq-10' : extraClassNamesUnwrapped}>
        {_.map(withDisplayNumbers, (def) => renderIt(def))}
        <div className={clsx(buttonExtraClassNames, { pb10: wrapInPanel })}>
          <CancelAndExecute
            cancelAction={closeFn}
            submitAction={async () => {
              await Promise.resolve().then(handleSubmit);
            }}
            toolId={toolId}
            btnDisabled={!enableSubmitBtn || !!submitBtnDisabled}
            cancelBtnLabel={cancelBtnLabel}
            submitBtnLabel={submitBtnLabel}
            hideCancel={hideCancel}
            submitBtnId={submitBtnId}
            hideExecute={hideExecute}
            submitOptions={handleSubmitOptions}
            activeSubmitKey={activeSubmitKey}
            setActiveSubmitKey={setActiveSubmitKey}
          />
        </div>
      </div>
    );
  };

  const renderSaveAndCancelForm = (handleSubmit: SubmitHandler, enableSubmitBtn: boolean, values: FormValues) => (
    <>
      {_.map(withDisplayNumbers, (def) => renderIt(def))}
      {!(hideCancel && hideSubmit) ? (
        <div
          className={clsx(
            'flexColumnContainer mt30 tw:items-center tw:pb-sq-10',
            leadingButton ? 'tw:justify-between' : 'tw:justify-center tw:gap-2',
            buttonExtraClassNames,
          )}>
          {leadingButton}
          <div className="flexColumnContainer tw:items-center tw:gap-2">
            <CancelSaveAndContinue
              submitFn={() =>
                Promise.resolve()
                  .then(handleSubmit)
                  .then(() => undefined)
              }
              cancelFn={closeFn}
              values={values}
              btnDisabled={!enableSubmitBtn || !!submitBtnDisabled}
              cancelBtnLabel={cancelBtnLabel}
              cancelClassNames={cancelClassNames}
              submitBtnLabel={submitBtnLabel}
              submitBtnVariant={submitBtnVariant}
              hideSubmit={hideSubmit}
              hideCancel={hideCancel}
              continueProcessing={continueProcessing}
              continueButtonLabel={continueButtonLabel}
              continueButtonTooltip={continueButtonTooltip}
              continueButtonVariant={continueButtonVariant}
              continueButtonPosition={continueButtonPosition}
              continueButtonExtraClassNames={continueButtonExtraClassNames}
              continueButtonEnabled={continueButtonEnabled}
              continueButtonOnSubmit={
                continueButtonOnSubmit && continueButtonShouldValidate
                  ? () =>
                      Promise.resolve()
                        .then(handleSubmit)
                        .then(() => undefined)
                  : continueButtonOnSubmit
              }
              continueButtonIcon={continueButtonIcon}
              setIsContinueButtonClicked={setIsExtraButton}
              submitOptions={submitOptions}
              resetToFirstSubmitOptionOnSubmit={resetToFirstSubmitOptionOnSubmit}
              hideContinue={hideContinue}
            />
          </div>
        </div>
      ) : null}
    </>
  );

  const getActiveSubmitFn = (values: FormValues) => {
    if (submitOptions && activeSubmitKey) {
      const activeOption = submitOptions.find((opt) => opt.key === activeSubmitKey);
      if (activeOption) return activeOption.submitFn(values);
    }
    return submitFn(values);
  };

  const renderForm = () => {
    return (
      <Form<FormValues>
        onSubmit={(values: FormValues) => {
          // to ensure that form validation works correctly for the "extra button" we need to make sure we still
          // follow the final form data-flow and use a flag in the submit function to determine which function to call
          if (!continueButtonOnSubmit) return getActiveSubmitFn(values) as unknown as void;
          if (isExtraButton) return continueButtonOnSubmit(values) as unknown as void;
          return getActiveSubmitFn(values) as unknown as void;
        }}
        initialValues={formData}
        render={({ handleSubmit, values, errors, submitFailed, modified, form }) => {
          /**
           * the submit button should be enabled if:
           *  - the form is pristine (no fields have been modified)
           *  - if the form has already been submitted once, then the button should only be enabled
           *    if there are no errors
           *  - if the form has not been submitted, and all modified fields are valid
           **/
          const modifiedFields = _.filter(_.keys(modified ?? {}), (key) => ((modified ?? {})[key] ? key : null));
          const defaultProvidedFields = _.map(
            _.filter(flatFormDefinition, (item) => (item.defaultProvided ? item.name : null)),
            'name',
          );
          const modifiedFieldsWithErrors = _.filter(_.keys(errors), (error) =>
            _.includes(modifiedFields, error) ? error : null,
          );
          const defaultProvidedFieldsWithErrors = _.filter(_.keys(errors), (error) =>
            _.includes(defaultProvidedFields, error) ? error : null,
          );
          const enableSubmitBtn = submitFailed
            ? _.size(errors) < 1
            : _.size(modifiedFieldsWithErrors) < 1 && _.size(defaultProvidedFieldsWithErrors) < 1;

          if (formRef) {
            formRef.current = enableSubmitBtn;
          }

          return (
            <form
              onSubmit={handleFormSubmit}
              data-testid={testId || toolId}
              className={clsx({ simpleSaveForm: saveAndCancel }, extraClassNames)}>
              <SubmitDisableEffect
                enableSubmitBtn={enableSubmitBtn}
                setSubmitIsDisabled={setSubmitIsDisabled}
                onDirtyChange={onDirtyChange}
              />
              {!saveAndCancel && renderToolPanelForm(handleSubmit as SubmitHandler, enableSubmitBtn)}
              {saveAndCancel && renderSaveAndCancelForm(handleSubmit as SubmitHandler, enableSubmitBtn, values)}
              {debug && (
                <>
                  <pre>{JSON.stringify(values, null, 2)}</pre>
                  <pre>{JSON.stringify(errors, null, 2)}</pre>
                </>
              )}
            </form>
          );
        }}
      />
    );
  };

  return renderForm();
});
